// ============================================================
// SUPPRESSING FIRE - who the burst actually catches.
//
// A suppression burst is not aimed at anybody: it is sprayed across an
// arc to make everyone in it keep their heads down. Two consequences
// this module owns.
//
// 1. THE HITS ARE SCATTERED, not concentrated. Each hit picks a victim
//    at random, so a burst of three into four people may put all three
//    into one of them or one each into three - which is the point.
// 2. EVERYONE IN THE ZONE TESTS, hit or not. The Willpower test comes
//    from engine/pinning, at Hard (-20).
//
// The die comes from engine/dice, so the E2E's seeded sequence covers
// the assignment too. ROLL ORDER IS A CONTRACT: one d10 per hit, in
// hit order, BEFORE any per-victim damage - see dev-docs/combat-engine.
//
// Pure. Zero React dependency.
// ============================================================
import { rollDie } from './dice';

/**
 * Deal `hits` among `victimIds`, one random pick per hit.
 *
 * Returns a count PER ID, including zeros: a caller resolving a volley
 * needs to know who was in the zone and untouched, because they still
 * test against being pinned.
 *
 * A d10 is used rather than a raw uniform so the seeded E2E sequence can
 * name the picks the way it names every other roll. The modulo is fair
 * for the group sizes a table produces (a d10 across 3 people is 4/3/3);
 * a burst is not a lottery and nobody is counting the bias.
 */
export function assignHitsRandomly(hits: number, victimIds: readonly string[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const id of victimIds) out[id] = 0;
  if (!victimIds.length || hits <= 0) return out;
  for (let i = 0; i < hits; i++) {
    const pick = victimIds[(rollDie(10) - 1) % victimIds.length];
    out[pick] += 1;
  }
  return out;
}
